<template>
  <div>
    <div
      class="dropzone"
      @dragover.prevent
      @dragenter.prevent
      @drop.prevent="handleDrop"
      @click="handleClick"
    >
      <input
        type="file"
        ref="fileInput"
        :multiple="multiple"
        :accept="fileType"
        @change="handleFilesSelected"
        style="display: none"
      />
      <FeatherIcon :icon="'UploadIcon'" size="24" />
      <span>{{ $t('Drag and drop files here, or click to select files') }}</span>
    </div>
    <div v-if="selectedFiles.length" class="preview">
      <div
        v-for="(file, index) in selectedFiles"
        :key="index"
        class="file-item d-flex justify-content-between align-items-center"
        style="gap: 12px"
      >
        <div class="d-flex align-items-center" style="gap: 12px">
          <img v-if="isImage(file)" :src="file.url" :alt="file.name" class="file-thumbnail" />
          <FeatherIcon v-else :icon="'FileIcon'" size="24" />
          <span>{{ file.name }} ({{ formatFileSize(file.size) }})</span>
        </div>
        <b-button variant="danger" type="button" size="sm" @click="removeItem(index)">
          <FeatherIcon :icon="'XIcon'" size="14" />
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue';

import { helper } from '@core/mixins/helper.js';

export default {
  components: {
    BButton
  },
  mixins: [helper],
  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    fileType: {
      type: String,
      default: '' // e.g., 'image/*', '.pdf', '.docx', etc.
    },
    maxFileSize: {
      type: Number,
      default: 5 * 1024 * 1024 // 5MB by default
    }
  },
  data() {
    return {
      selectedFiles: []
    };
  },
  methods: {
    removeItem(index) {
      this.selectedFiles = this.selectedFiles.filter((_, innerIndex) => index !== innerIndex);
      this.$emit('filesSelected', this.selectedFiles);
    },
    handleDrop(event) {
      const { files } = event.dataTransfer;
      this.processFiles(files);
    },
    handleClick() {
      this.$refs.fileInput.click();
    },
    handleFilesSelected(event) {
      const { files } = event.target;
      this.processFiles(files);
      this.$refs.fileInput.value = '';
    },
    processFiles(files) {
      const validFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Check file size
        if (file.size > this.maxFileSize) {
          this.showErrorMessage(
            this.$t(`uploadedFileTooLarge`, {
              fileName: file.name,
              fileSize: this.formatFileSize(this.maxFileSize)
            })
          );
        } else if (this.fileType && this.fileType.includes(file.type)) {
          // Check file type
          this.showErrorMessage(
            this.$t(`uploadedFileNotValidType`, { fileName: file.name, fileType: this.fileType })
          );
        } else {
          // If file is valid, create a URL for image preview
          file.url = URL.createObjectURL(file);
          validFiles.push(file);
        }
      }

      this.selectedFiles = validFiles;
      this.$emit('filesSelected', validFiles);
    },
    isImage(file) {
      return file.type.startsWith('image/');
    },
    formatFileSize(size) {
      const kb = size / 1024;
      const mb = kb / 1024;
      if (mb > 1) return `${mb.toFixed(2)} MB`;
      return `${kb.toFixed(2)} KB`;
    }
  }
};
</script>

<style scoped>
.dropzone {
  border: 2px dashed #ccc;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
}

.preview {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
}

.file-item {
  border: 1px solid #eee;
  padding: 10px;
  text-align: center;
  display: flex;
  gap: 16px;
  align-items: center;
}

.file-thumbnail {
  max-width: 100px;
  max-height: 100px;
  margin-bottom: 5px;
}
</style>
